import TextField from 'ui/elements/form/TextField';
import React, { useEffect } from 'react';

import ConfirmDialog from 'ui/modules/dialogs/ConfirmDialog';
import Button from 'ui/elements/buttons/Button';
import config from 'config';
import Switch from '../../choice/Switch';
import PlusIcon from 'ui/elements/icons/PlusIcon';

export default function CompanyProfileDialog({
  isOpen,
  closeDialog,
  onConfirm,
  selectedElement,
}: {
  isOpen: boolean;
  closeDialog: () => void;
  onConfirm: (urls: string[], isDetailed: boolean, isGrid: boolean) => void;
  selectedElement?: HTMLElement;
}) {
  const [profiles, setProfiles] = React.useState<string[]>(['']);
  const [isDetailedView, setIsDetailedView] = React.useState<boolean>(true);
  const [isGridView, setIsGridView] = React.useState<boolean>(false);
  const [touched, setTouched] = React.useState<boolean[]>([]);
  const errors = profiles.map(profile => {
    if (!profile) return 'Enter a url';
    if (!profile.startsWith(config.PORTAL_URL + '/companies/'))
      return `Enter a company url, e.g. ${config.PORTAL_URL}/companies/acme-inc`;
    return undefined;
  });

  useEffect(() => {
    if (!selectedElement) {
      setProfiles(['']);
      return;
    }
    const pre = selectedElement?.querySelectorAll('iframe');
    const srcs = pre?.length ? Array.from(pre).map(p => p.getAttribute('src') || '') : [''];
    const slugs = srcs.map(url => url.match(/\/companies\/([\w-]+)/)?.[1] || '');
    const urls = slugs.map(slug => `${config.PORTAL_URL}/companies/${slug}`);

    const isGridView = selectedElement?.classList.contains('ql-company-embed-grid') ?? false;
    const isDetailedView = selectedElement?.classList.contains('ql-company-embed-detailed') ?? true;

    setProfiles(urls);
    setIsDetailedView(isDetailedView);
    setIsGridView(isGridView);
  }, [selectedElement]);

  return (
    <ConfirmDialog
      maxWidth="sm"
      title="Embed company profiles"
      isOpen={isOpen}
      onClose={closeDialog}
      dismissLabel="Cancel"
      confirmButton={
        <Button kind="primary" onClick={() => onConfirm(profiles, isDetailedView, isGridView)}>
          {selectedElement ? 'Save' : 'Insert'}
        </Button>
      }
    >
      <p className="u-content-spacing-bottom">Add urls to company profiles you would like to embed.</p>
      <div className="u-content-spacing-bottom">
        <Switch label="Detailed view" value={isDetailedView} onChange={() => setIsDetailedView(!isDetailedView)} />
        <Switch label="Grid view" value={isGridView} onChange={() => setIsGridView(!isGridView)} />
      </div>
      {profiles.map((profile, index) => (
        <TextField
          className="u-half-spacing-bottom"
          key={index}
          fullWidth
          value={profile}
          onChange={e => {
            setProfiles([...profiles.slice(0, index), e.target.value, ...profiles.slice(index + 1)]);
          }}
          onBlur={() => setTouched([...touched.slice(0, index), true, ...touched.slice(index + 1)])}
          autoFocus={index === profiles.length - 1}
          placeholder={`${config.PORTAL_URL}/companies/<company-name>`}
          helperText={touched[index] && errors[index]}
          error={!!errors[index]}
        />
      ))}
      <Button
        size="small"
        className="u-content-spacing-top"
        kind="tertiary"
        onClick={() => setProfiles([...profiles, ''])}
      >
        <PlusIcon fontSize="small" className="u-quarter-spacing-right" /> Add another
      </Button>
    </ConfirmDialog>
  );
}
